import { useCallback, useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { STAGE_EVENTS } from '../../../constants';
import useStage from '../../../providers/hooks/use-stage';
import type { StagePointerEventT } from '../../../providers/stage-provider';
import { isLabelingMode } from '../../toolbar/utils';
import { calculateScaleTransform, isScaleVertexType } from './scaling-utils';
import type { ScalableDataT, UseShapeScalingPropsT } from './use-shape-scaling.types';
export const useShapeScaling = <T extends ScalableDataT,>({
  isDrawing,
  isEditable,
  isLocked,
  selectedScaleHandle,
  originalBoundingBox,
  currentData,
  annotationIdx,
  applyScaleFunction,
  onDataUpdate,
  onScaleComplete
}: UseShapeScalingPropsT<T>) => {
  const {
    stage
  } = useStage();
  const {
    editorModeAtom
  } = useStageAtoms();
  const editorMode = useAtomValue(editorModeAtom);
  const initialDataRef = useRef<T | null>(null);
  const currentDataRef = useRef<T | null>(currentData);

  // eslint-disable-next-line react-hooks/refs
  currentDataRef.current = currentData;
  const onDrag = useCallback((stageEvent: StagePointerEventT) => {
    if (!selectedScaleHandle || !originalBoundingBox || !currentDataRef.current || !isScaleVertexType(selectedScaleHandle)) {
      return;
    }
    if (!initialDataRef.current) {
      initialDataRef.current = currentDataRef.current;
    }
    const mousePos = stageEvent.detail.mousePos;
    const transform = calculateScaleTransform(selectedScaleHandle, originalBoundingBox, mousePos);
    if (!transform) {
      return;
    }
    const scaledData = applyScaleFunction(initialDataRef.current, transform);
    onDataUpdate(scaledData);
  }, [selectedScaleHandle, originalBoundingBox, applyScaleFunction, onDataUpdate]);
  const onDragEnd = useCallback((stageEvent: StagePointerEventT) => {
    if (!selectedScaleHandle || !originalBoundingBox || !currentDataRef.current || !annotationIdx || !isScaleVertexType(selectedScaleHandle)) {
      return;
    }
    const mousePos = stageEvent.detail.mousePos;
    const transform = calculateScaleTransform(selectedScaleHandle, originalBoundingBox, mousePos);
    if (!transform) {
      return;
    }
    const scaledData = applyScaleFunction(initialDataRef.current ?? currentDataRef.current, transform);
    initialDataRef.current = null;
    onScaleComplete(scaledData);
  }, [selectedScaleHandle, originalBoundingBox, annotationIdx, applyScaleFunction, onScaleComplete]);
  useEffect(() => {
    if (!stage || isDrawing || !isEditable || isLocked || !isLabelingMode(editorMode)) {
      return;
    }
    if (selectedScaleHandle && isScaleVertexType(selectedScaleHandle)) {
      stage.addEventListener(STAGE_EVENTS.DRAG, onDrag);
      stage.addEventListener(STAGE_EVENTS.DRAG_END, onDragEnd);
      return () => {
        stage.removeEventListener(STAGE_EVENTS.DRAG, onDrag);
        stage.removeEventListener(STAGE_EVENTS.DRAG_END, onDragEnd);
      };
    }
  }, [stage, isDrawing, isEditable, isLocked, selectedScaleHandle, onDrag, onDragEnd, editorMode]);
  return {
    isScaling: selectedScaleHandle !== null && isScaleVertexType(selectedScaleHandle)
  };
};